import { useEffect, useReducer } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import axios from "axios";
import { ACTIONS } from "./executionActions";
import { executionsReducer, initialState } from "./executionsReducer";

import ExecutionsCycleList from "./ExecutionsCycleList";

import "./Execution.css";
import LoadingSpinner from "../../LoadingSpinner";

export default function ExecutionsGate({
  testAppCredentials,
  testAppName,
  projectId,
  projectKey,
  projectName,
  versionId,
  versionName,
}) {
  const [state, dispatch] = useReducer(executionsReducer, initialState);

  const { email, accessKey, secretKey, tmType } = testAppCredentials || {};

  const getCycles = async (action, credentials, params = null) => {
    const apiUrl = `${window.configs.BASE_API_URL}${window.configs.AIQA_ZEPHYR_HANDLER_URL}`;

    const payload = {
      action,
      credentials: { access_key: accessKey, secret_key: secretKey },
      ...(params && Object.keys(params).length > 0 && { params }),
    };
    try {
      const response = await axios.post(apiUrl, payload);
      if (response.data?.status !== "success") {
        throw new Error(response.data?.message || "API Error");
      }
      return response.data;
    } catch (err) {
      console.error("API Error:", err);
      throw err;
    }
  };

  const uploadReport = async (formData) => {
    const apiUrl = `${window.configs.BASE_API_URL}${window.configs.AIQA_UPLOAD_RTM_URL}`; // adjust

    return axios.post(apiUrl, formData, {
      headers: {
        Authorization: sessionStorage.getItem("authToken"),
        "ch-subscription-key": JSON.parse(sessionStorage.getItem("UserDetails"))
          ?.data?.User?.["ch-subscription-key"],
      },
    });
  };

  /* ---------------- Fetch Cycles ---------------- */
  useEffect(() => {
    if (!projectId || !versionId) return;

    if (!accessKey || !secretKey || !tmType) {
      toast.warn("Please add test app credentials in settings");
      dispatch({ type: ACTIONS.FETCH_CYCLES_SUCCESS, payload: [] }); // stop loader
      return;
    }

    dispatch({ type: ACTIONS.FETCH_CYCLES_START });

    getCycles("get_cycles", testAppCredentials, {
      project_id: projectId,
      version_id: versionId,
    })
      .then((res) => {
        if (res.status === "success") {
          dispatch({
            type: ACTIONS.FETCH_CYCLES_SUCCESS,
            payload: res.info.map((c) => ({
              cycleId: c.id,
              cycleName: c.name,
              environment: c.environment,
              build: c.build,
              executions: [],
              executionsLoaded: false,
              loading: false,
              exportLoading: false,
              uploadLoading: false,
              pagination: {
                offset: 0,
                size: 25,
                hasMore: true,
              },
            })),
          });
        } else {
          //  API returned failure
          throw new Error(res.message || "Failed to fetch cycles");
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("Something went wrong while getting the cycles");

        //  IMPORTANT: stop loading
        dispatch({ type: ACTIONS.FETCH_CYCLES_SUCCESS, payload: [] });
      });
  }, [projectId, versionId, accessKey, secretKey, tmType]);

  /* ---------------- Fetch Executions ---------------- */
  const fetchExecutions = async (cycleId) => {
    const cycle = state.cycles.find((c) => c.cycleId === cycleId);
    if (!cycle || cycle.loading || !cycle.pagination.hasMore) return;

    dispatch({ type: ACTIONS.FETCH_EXECUTIONS_START, cycleId });

    const res = await getCycles("get_executions_by_cycle", testAppCredentials, {
      project_id: projectId,
      version_id: versionId,
      cycle_id: cycleId,
      offset: cycle.pagination.offset,
      size: cycle.pagination.size,
    });

    if (res.status !== "success") return;

    const list = res.info?.searchResult?.searchObjectList || [];

    const executionList = list.map((item) => {
      const exec = item.execution;
      return {
        executionId: exec.id,
        testKey: item.issueKey,
        testSummary: item.issueSummary,
        testLabels: item.issueLabel,
        defects: exec.defects,
        status: exec.status?.name,
        statusColor: exec.status?.color,
        executedBy: item.executedByDisplayName,
        executedOn: exec.executedOn,
        executedOnDisplay: exec.executedOn
          ? new Date(exec.executedOn).toLocaleString()
          : "",
        assignedTo: item.assignedToDisplayName,
      };
    });

    dispatch({
      type: ACTIONS.FETCH_EXECUTIONS_SUCCESS,
      cycleId,
      payload: executionList,
    });
  };

  /* ---------------- Export ---------------- */
  const exportCycleExcel = async (cycle) => {
    dispatch({ type: ACTIONS.EXPORT_CYCLE_START, cycleId: cycle.cycleId });

    try {
      const rows = cycle.executions.map((e) => ({
        "Version ID": versionId,
        "Version Name": versionName,
        "Cycle ID": cycle.cycleId,
        "Cycle Name": cycle.cycleName,
        "Execution ID": e.executionId,
        "Test Key": e.testKey,
        Summary: e.testSummary,
        Status: e.status,
        "Executed By": e.executedBy || "",
        "Executed On": e.executedOn ? new Date(e.executedOn).toISOString() : "",
        Assignee: e.assignedTo || "",
        Defects: e.defects?.map((d) => d.key).join(", ") || "",
        Environment: cycle.environment || "",
        Build: cycle.build || "",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "executionRtm");

      XLSX.writeFile(
        wb,
        `${projectKey}-${versionName}-${cycle.cycleName}-executions-${new Date().toISOString()}.xlsx`,
      );
    } finally {
      dispatch({ type: ACTIONS.EXPORT_CYCLE_END, cycleId: cycle.cycleId });
    }
  };

  /* ---------------- Upload ---------------- */
  const uploadCycleExcel = async (cycle, target) => {
    dispatch({ type: ACTIONS.UPLOAD_CYCLE_START, cycleId: cycle.cycleId });

    try {
      const rows = cycle.executions.map((item) => ({
        "Version ID": versionId,
        "Version Name": versionName,
        "Cycle ID": cycle.cycleId,
        "Cycle Name": cycle.cycleName,
        "Execution ID": item.executionId,
        "Test Key": item.testKey,
        Summary: item.testSummary,
        Status: item.status,
        ExecutedBy: item.executedBy || "",
        "Executed On": item.executedOn
          ? new Date(item.executedOn).toISOString()
          : "",
        Assignee: item.assignedTo || "",
        Defects: item.defects?.map((d) => d.key).join(", ") || "",
        Environment: cycle.environment || "",
        Build: cycle.build || "",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "executionRtm");

      const fileName = `${projectKey}-${versionName}-${cycle.cycleName}-executions-${new Date().toISOString().replace(/[:.]/g, "-")}.xlsx`;

      const wbArray = XLSX.write(wb, {
        type: "array",
        bookType: "xlsx",
      });
      const blob = new Blob([wbArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Create File object
      const file = new File([blob], fileName, { type: blob.type });

      const formData = new FormData();
      formData.append("file", file);
      formData.append("fileName", fileName);
      formData.append("projectKey", projectKey);
      formData.append("projectName", projectName);
      formData.append("target", target);
      formData.append("templateType", "executionRtm");

      await uploadReport(formData);
      toast.success("Upload successful");

    } catch (e) {
      toast.error("Upload failed");
    } finally {
      dispatch({ type: ACTIONS.UPLOAD_CYCLE_END, cycleId: cycle.cycleId });
    }
  };

  if (state.loading)
    return (
      <div>
        <LoadingSpinner small message="Loading Cycles..." />
      </div>
    );

  return (
    <ExecutionsCycleList
      cycles={state.cycles}
      onFetchExecutions={fetchExecutions}
      onExportCycle={exportCycleExcel}
      onUploadCycle={uploadCycleExcel}
    />
  );
}
